import { useMemo } from 'react'
import { layoutBlocks, clockLabel, hourLabel, minutesLabel, TIMELINE_KINDS, GAP_MIN_MINUTES } from '../lib/dayTimeline.js'

// ONE DAY AS A CALENDAR COLUMN. Hours down the left, every timestamped thing
// the member did as a block at its minute (a call as tall as it lasted, a
// meeting as tall as its span, a sent email a slim chip), and the idle
// stretches between them drawn as hatched bands with their length written
// on them — because the gaps are what this chart exists to show. The
// arithmetic (minutes, gaps, lanes, bounds) lives in lib/dayTimeline.js and
// is node-tested; this file only draws what it is handed.

// Fixed hues, not theme tokens: a block is a filled chip with white text and
// must read the same in both themes. Calls take the brand blue; the three
// draft-working events share one hue (they are one story), sign-ins are
// grey because they are context, not work.
export const KIND_COLORS = {
  call: '#3C87F7',
  meeting: '#7c3aed',
  email: '#16a34a',
  note: '#d97706',
  task: '#64748b',
  deal: '#0d9488',
  run: '#db2777',
  question: '#0e7490',
  copy: '#0e7490',
  open: '#0e7490',
  signin: '#94a3b8',
}

const PX_PER_MIN = 1.2 // 72px per hour: an hour tall enough to read three chips in
const MIN_BLOCK_PX = 18 // a point event still needs one line of text
const FOOTPRINT_MIN = MIN_BLOCK_PX / PX_PER_MIN // the overlap footprint of a chip, in minutes
const GAP_LABEL_PX = 30 // a gap band shows its label only when this tall
const HATCH = 'repeating-linear-gradient(45deg, transparent 0 7px, var(--border) 7px 8px)'

function blockTitle(b) {
  const when = b.durationMin > 0 ? `${clockLabel(b.startMin)} – ${clockLabel(b.endMin)} (${minutesLabel(b.durationMin)})` : clockLabel(b.startMin)
  const parts = [when, b.label]
  if (b.detail) parts.push(b.detail)
  if (b.assumed) parts.push('end time assumed — HubSpot recorded none')
  return `${parts.join(' · ')}. Press to open the rows behind it.`
}

export default function DayTimeline({ timeline, onOpen }) {
  const { bounds, blocks, gaps, nowMin } = timeline
  const laid = useMemo(() => layoutBlocks(blocks, { footprintMin: FOOTPRINT_MIN }), [blocks])
  const { startMin, endMin } = bounds
  const totalMin = Math.max(60, endMin - startMin)
  const height = totalMin * PX_PER_MIN
  const y = (min) => (Math.min(endMin, Math.max(startMin, min)) - startMin) * PX_PER_MIN
  const hours = []
  for (let h = Math.ceil(startMin / 60); h * 60 <= endMin; h += 1) hours.push(h)
  const present = TIMELINE_KINDS.filter((k) => timeline.counts[k.key])

  return (
    <div>
      {/* The legend names only the kinds on this day, with their counts, so
          it doubles as the day's tally. */}
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-dim">
        {present.map((k) => (
          <span key={k.key} className="inline-flex items-center gap-x-1.5">
            <span className="inline-block h-[10px] w-[10px] rounded-[3px]" style={{ background: KIND_COLORS[k.key] }} aria-hidden="true" />
            {k.label} <span className="text-faint">{timeline.counts[k.key].toLocaleString()}</span>
          </span>
        ))}
        {gaps.length > 0 && (
          <span className="inline-flex items-center gap-x-1.5">
            <span className="inline-block h-[10px] w-[10px] rounded-[3px] border border-line" style={{ backgroundImage: HATCH }} aria-hidden="true" />
            Gap of {GAP_MIN_MINUTES}m or more
          </span>
        )}
      </div>
      <div className="flex" style={{ height: height + 14 }}>
        <div className="relative w-[58px] flex-none">
          {hours.map((h) => (
            <span key={h} className="absolute right-2 -translate-y-1/2 whitespace-nowrap text-[11px] tabular-nums text-faint" style={{ top: y(h * 60) }}>
              {hourLabel(h)}
            </span>
          ))}
        </div>
        <div className="relative min-w-0 flex-1 border-l border-line" style={{ height }}>
          {hours.map((h) => (
            <div key={h} className="absolute left-0 right-0 border-t border-dashed border-line" style={{ top: y(h * 60) }} aria-hidden="true" />
          ))}
          {/* THE GAPS, drawn before the blocks so they sit behind them. Each
              carries its length and its edges; a band too short for the label
              still says so on hover. */}
          {gaps.map((g) => {
            const top = y(g.startMin)
            const h = y(g.endMin) - top
            return (
              <div
                key={`gap-${g.startMin}`}
                className="absolute left-0 right-0 flex items-center justify-center"
                style={{ top, height: h, backgroundImage: HATCH }}
                title={`No activity logged from ${clockLabel(g.startMin)} to ${clockLabel(g.endMin)} — ${minutesLabel(g.minutes)}.`}
              >
                {h >= GAP_LABEL_PX && (
                  <span className="rounded-full border border-line bg-surface px-2 py-[2px] text-[11px] font-medium text-dim">
                    {minutesLabel(g.minutes)} gap · {clockLabel(g.startMin)} – {clockLabel(g.endMin)}
                  </span>
                )}
              </div>
            )
          })}
          {laid.map((b) => {
            const top = y(b.startMin)
            const h = Math.max(MIN_BLOCK_PX, y(b.endMin) - top)
            const width = 100 / (b.lanes || 1)
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => onOpen && onOpen(b)}
                title={blockTitle(b)}
                className="absolute z-[1] cursor-pointer overflow-hidden rounded-[6px] border-none px-2 py-[1px] text-left text-[11.5px] leading-[16px] text-white shadow-sm transition-[filter] hover:brightness-110 focus-visible:[outline:2px_solid_var(--text)]"
                style={{
                  top,
                  height: h,
                  left: `calc(${b.lane * width}% + 6px)`,
                  width: `calc(${width}% - 10px)`,
                  background: KIND_COLORS[b.kind] || 'var(--muted)',
                }}
              >
                <div className="truncate font-semibold">
                  {clockLabel(b.startMin)} · {b.label}
                </div>
                {h >= 34 && b.detail ? <div className="truncate opacity-90">{b.detail}</div> : null}
              </button>
            )
          })}
          {/* NOW, on today only: the line every calendar draws, so the empty
              column below it reads as "not yet" rather than "nothing". */}
          {nowMin != null && nowMin >= startMin && nowMin <= endMin && (
            <div className="absolute left-0 right-0 z-[2] border-t-2 border-danger" style={{ top: y(nowMin) }} title={`Now · ${clockLabel(nowMin)}`}>
              <span className="absolute -left-[5px] -top-[5px] h-[8px] w-[8px] rounded-full bg-danger" aria-hidden="true" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
